import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const LOGO_URL = "/cgi-logo.png";
const CGI_WEBSITE = "https://www.commongroundinstitute.net";

export default function Register() {
  const { user, register, formatApiErrorDetail } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await register(name.trim(), email.trim(), password);
      toast.success("Account created. Welcome.");
      nav("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-10 bg-clinical" data-testid="register-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex flex-col items-start gap-3 mb-10">
          <a href={CGI_WEBSITE} target="_blank" rel="noopener noreferrer" aria-label="Visit the Common Ground Institute website">
            <img src={LOGO_URL} alt="Common Ground Institute" className="h-20 w-auto object-contain rounded-md" />
          </a>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mt-4">Create your account</h1>
          <p className="text-slate-600 leading-relaxed">
            Track your growth across empathic listening, calm expression, and composure under pressure.
          </p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name" required value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" autoComplete="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password" autoComplete="new-password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="register-password-input"
                  testIdPrefix="register-password"
                />
                <p className="text-xs text-slate-500">At least 6 characters.</p>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="register-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="register-submit-button"
              >
                {submitting ? "Creating account…" : "Create account"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-slate-600 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-to-login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
